<template>
  <div class="list-container"> <!-- TODO Check that everything is removed from CreateReport that is not necessary -->
    <div :class="['header-container', isOpen ? 'expanded' : undefined, hideMarginsWhenClosed ? 'hide-closed-margins' : undefined]" v-if="showHeader">
      <ion-item lines="full" @click="toggleList" button detail="false">
        <ion-icon slot="start" :icon="chevronForward"></ion-icon>
        <slot slot="default" name="label">
          <ion-label class="default-title">
            {{ title }}
          </ion-label>
        </slot>
        <ion-badge v-if="primaryIndicator != null && (primaryIndicator > 0 || showZeroIndicator)" slot="end" color="primary" :class="(showZeroIndicator && primaryIndicator <= 0) ? 'invert' : undefined">
          {{ primaryIndicator }}<ion-icon v-if="primaryIcon != null" :icon="primaryIcon"></ion-icon>
        </ion-badge>
        <ion-badge v-if="tertiaryIndicator != null && (tertiaryIndicator > 0 || showZeroIndicator)" slot="end" color="tertiary" :class="(showZeroIndicator && tertiaryIndicator <= 0) ? 'invert' : undefined">
          {{ tertiaryIndicator }}<ion-icon v-if="tertiaryIcon != null" :icon="tertiaryIcon"></ion-icon>
        </ion-badge>
        <ion-badge v-if="successIndicator != null && (successIndicator > 0 || showZeroIndicator)" slot="end" color="success" :class="(showZeroIndicator && successIndicator <= 0) ? 'invert' : undefined">
          {{ successIndicator }}<ion-icon v-if="successIcon != null" :icon="successIcon"></ion-icon>
        </ion-badge>
        <ion-badge v-if="dangerIndicator != null && (dangerIndicator > 0 || showZeroIndicator)" slot="end" color="danger" :class="(showZeroIndicator && dangerIndicator <= 0) ? 'invert' : undefined">
          {{ dangerIndicator }}<ion-icon v-if="dangerIcon != null" :icon="dangerIcon"></ion-icon>
        </ion-badge>
        <ion-badge v-if="subIndicator != null && (subIndicator > 0 || showZeroIndicator)" slot="end" color="sub-icon" :class="(showZeroIndicator && subIndicator <= 0) ? 'invert' : undefined">
          {{ subIndicator }}<ion-icon v-if="subIcon != null" :icon="subIcon"></ion-icon>
        </ion-badge>
        <slot slot="end" name="end"></slot>
      </ion-item>
    </div>
    <div class="outer-scroll-area-container">
      <div ref="scrollContainer" @scroll.passive="(event) => scrollable ? scrollContent(event) : null" :class="['inner-scroll-area-container', ...(scrollable ? ['no-scrollbar'] : [undefined])]">
        <div ref="itemsContainer" :class="['items-container', (isOpen ? 'expanded' : undefined)]" @invalid.capture="openListWhenInvalid()">
          <slot></slot>
          <ion-item v-if="showNoItemsIndicator" lines="full" class="no-items-indicator">
            <ion-label>{{ (noItemsIndicator != null) ? noItemsIndicator : i18n.$t('default_interaction.no_items') }}</ion-label>
          </ion-item>
        </div>
      </div>
      <div v-if="scrollable" :class="['custom-scrollbar', showHeader ? 'top-aligned' : undefined]"></div> <!-- If title is visible remove top padding as the corner is not curved -->
    </div>
  </div>
</template>


<script>
import { IonItem, IonIcon, IonBadge, IonLabel } from '@ionic/vue';
import { computed, onUnmounted, ref, watch } from 'vue';

import { chevronForward } from 'ionicons/icons';

import { useI18n } from "@/utils/i18n";

import { createCustomScrollbarHandlers } from '@/utils/interaction';

export default  {
  name: 'CollapsibleList',
  components: { IonItem, IonBadge, IonIcon, IonLabel },
  props: {
    open: {
      type: Boolean,
      default: false
    },
    showHeader: {
      type: Boolean,
      default: true
    },
    title: String,
    primaryIndicator: Number,
    primaryIcon: [Object, String],
    tertiaryIndicator: Number,
    tertiaryIcon: [Object, String],
    successIndicator: Number,
    successIcon: [Object, String],
    dangerIndicator: Number,
    dangerIcon: [Object, String],
    subIndicator: Number,
    subIcon: [Object, String],
    showNoItemsIndicator: {
      type: Boolean,
      default: false
    },
    showZeroIndicator: {
      type: Boolean,
      default: false
    },
    noItemsIndicator: String,
    openOnInvalidEvent: {
      type: Boolean,
      default: false
    },
    hideMarginsWhenClosed: {
      type: Boolean,
      default: false
    },
    scrollable: {
      type: Boolean,
      default: false
    }
  },
  setup(props, { emit }){
    const i18n = useI18n();

    const isOpenProp = computed(() => props.open);

    const isOpen = ref(isOpenProp.value);

    watch((isOpen), (newState) => {
      emit('update:open', newState);
    });

    watch((isOpenProp), (newState) => {
      isOpen.value = newState;
    });

    const toggleList = function() {
      //Only allow toggle when header is visible
      if (props.showHeader) {
        isOpen.value = !(isOpen.value);
      }
    }

    const openListWhenInvalid = function() {
      if (props.openOnInvalidEvent) {
        isOpen.value = true;
      }
    }

    const scrollContainer = ref(null);

    const itemsContainer = ref(null);

    const openWhenScrolledToItem = function() {
      isOpen.value = true;
    }

    const { handleScrollContainerScroll, scrollContent, scrollbarResizeObserver } = createCustomScrollbarHandlers();

    //Attach scrollbar resize observer to update scrollbar state based on new size and set initial size
    watch(scrollContainer, (newContainer) => {
      if (props.scrollable) {
        scrollbarResizeObserver.disconnect(); //Disconnect all old ones
        if (newContainer != null) {
          scrollbarResizeObserver.observe(newContainer);
          handleScrollContainerScroll(newContainer); //Set initial size
        }
      }
    }, { immediate: true });

    //Attach listener on load or change and remove old ones. Instead of in onMounted!
    watch(itemsContainer, (newContainer, oldContainer) => {
      if (oldContainer != null) {
        oldContainer.removeEventListener('requestScrollToItem', openWhenScrolledToItem, { capture: true });
      }

      if (newContainer != null) {
        newContainer.addEventListener('requestScrollToItem', openWhenScrolledToItem, { capture: true });
      }
    }, { immediate: true });

    onUnmounted(() => {
      if (itemsContainer.value != null) {
        itemsContainer.value.removeEventListener('requestScrollToItem', openWhenScrolledToItem, { capture: true });
      }
      if (scrollbarResizeObserver != null) {
        scrollbarResizeObserver.disconnect();
      }
    });

    return {
      i18n,
      scrollContent,
      isOpen,
      scrollContainer,
      itemsContainer,
      toggleList,
      openListWhenInvalid,
      openWhenScrolledToItem,
      chevronForward
    };
  }
}
</script>

<style scoped>
ion-item {
  --background: var(--background);
}

.outer-scroll-area-container {
  display: flex;
  flex-direction: column;
  min-height: 0;
  position: relative;
  overflow: var(--custom-overflow, hidden);
}

.inner-scroll-area-container {
  flex-grow: 1;
  flex-shrink: 1;
  overflow: var(--custom-overflow, auto);
}

.header-container {
  cursor: pointer;
}

.header-container ion-item {
  --padding-start: var(--custom-padding-start, 12px);
  --border-width: var(--collapsible-header-border-width, 0 0 1px 0);
  --inner-padding-end: var(--custom-padding-end, 12px);
}

.header-container.hide-closed-margins ion-item {
  /* Override with 0 when given by props. So can either set through prop or with this CSS var. */
  --collapsible-header-border-width-collapsed: 0 0 0 0;
}

.header-container:not(.expanded) ion-item {
  /* Only change if set, otherwise take default value */
  --border-width: var(--collapsible-header-border-width-collapsed, var(--collapsible-header-border-width, 0 0 1px 0));
}

/* Animate Rotation of chevron icon */
.header-container ion-item > ion-icon {
  transition: transform 0.2s ease-in-out;
}
.list-container .header-container.expanded ion-item > ion-icon {
  transform: rotate(90deg);
}

.list-container .items-container {
  margin-left: var(--custom-item-margin, 0px);
}

/* Collapse items */
.list-container .items-container:not(.expanded){
  display: none;
}

.header-container > * {
  --color: var(--color, var(--ion-color-primary));
  --background: var(--background, unset);
  font-weight: bold;
}

.header-container ion-item > ion-icon {
  color: var(--custom-icon-color, var(--color));
  margin-inline-end: var(--custom-padding-start, 12px);
  margin-right: var(--custom-padding-start, 12px);
}

ion-badge {
  margin-left: 10px;
}

.ios ion-badge {
  margin-right: 0px;
}

ion-badge ion-icon {
  vertical-align: bottom;
  margin-left: 3px;
}

/* ion-badge.invert {
  background: var(--ion-color-light);
  color: var(--ion-color);
} */

.no-items-indicator {
  display: none;
  text-align: center;
  font-weight: var(--weight-level-light);
}

.no-items-indicator:only-child {
  display: unset;
}

.default-title {
  color: var(--custom-title-color, var(--color));
  font-size: var(--level-0-font-size)!important;
  font-weight: var(--weight-level-semibold)!important;
  white-space: normal;
}
</style>
